import {
  GLOBAL_ANALYTICS_EVENT_NAME,
  events, VIEW_ONLINE, ONLINE_VIEW,
} from './constants';
import type { GlobalAnalyticsEvent, EventSender, GlobalAnalyticsEventDetails } from './types';
import type {
  AddToCartEvent,
  DocumentDownloadEvent,
  RemoveFromCartEvent,
  PageViewEvent,
  PesAnalyticsType,
  GlobalAnalyticsEventExtraParams,
  CtaClickEventParams,
  RangeCtaClickEventParams,
  MediaGalleryCtaClickEventParams,
} from './types/events';
import { convertProductsPriceToFloat } from './number';
import {
  documentDownloadEvent,
  ctaClickEvent,
  refinementStateChangedEvent,
} from './events';
import { isAnalyticsEnabled } from './helpers';

declare global {
  interface Window {
    pesAnalytics: PesAnalyticsType;
  }
}

if (process.env.NODE_ENV === 'development') {
  window.addEventListener(
    GLOBAL_ANALYTICS_EVENT_NAME,
    (event: CustomEvent<GlobalAnalyticsEventDetails>): void => {
      // eslint-disable-next-line no-console
      console.info('📈 GA EVENT from UIaaS', event.detail);
    },
  );
}

const addToCart = (param: AddToCartEvent): AddToCartEvent => {
  const { products, ...rest } = param;
  const newProducts = convertProductsPriceToFloat(products);
  return { products: newProducts, ...rest };
};

const removeFromCart = (param: RemoveFromCartEvent): RemoveFromCartEvent => {
  const { products, ...rest } = param;
  const newProducts = convertProductsPriceToFloat(products);
  return { products: newProducts, ...rest };
};

const download = (param: DocumentDownloadEvent): DocumentDownloadEvent => {
  const { documentReference, documentId, documentName, ...rest } = param;
  return {
    ...rest,
    documentId: documentId || documentReference,
    documentName:
      documentName === ONLINE_VIEW || documentName === VIEW_ONLINE ? VIEW_ONLINE : documentName,
    event: events.DOWNLOAD,
  };
};

const virtualPageview = (data: PageViewEvent): PageViewEvent => {
  const { page } = data;
  if (page) {
    return data;
  }
  const currentUrl = window.decodeURI(window.location.href);
  return { ...data, page: currentUrl };
};

export const postProcessing = (data: GlobalAnalyticsEventDetails): GlobalAnalyticsEventDetails => {
  switch (data.event) {
    case events.VIRTUAL_PAGE_VIEW:
      return virtualPageview(data);
    case events.ADD_TO_CART:
      return addToCart(data);
    case events.DOWNLOAD:
    case events.DOWNLOAD_DOCUMENT:
      return download(data);
    case events.REMOVE_FROM_CART:
      return removeFromCart(data);
    default:
      return data;
  }
};

export const send: EventSender = (eventDetailsBuilder, params) => {
  const event = new CustomEvent(GLOBAL_ANALYTICS_EVENT_NAME, {
    detail: postProcessing(eventDetailsBuilder(params)),
    bubbles: true,
    composed: true,
  });

  window.dispatchEvent(event);
};

/**
 * Returns value of a param from the url
 * @paramName can be a list of params divided through | . In this case, returns the first match.
 * E.g. p_File_Name or p_File_Name|p_enDocType
 */
export const getParamValueFromURL = (url: string, paramName: string): string | undefined => {
  const regExp = new RegExp(`[?&](${paramName})=([^&]+).*$`);
  return url.match(regExp)?.[2];
};

export const externalLinkHandler = (link: string): void => {
  const documentId = getParamValueFromURL(link, 'p_Doc_Ref');
  if (!documentId) {
    return;
  }

  const documentName = getParamValueFromURL(link, 'p_File_Name|p_Archive_Name|p_FileName') ?? '';
  const documentType = getParamValueFromURL(link, 'p_enDocType') ?? '';

  send(documentDownloadEvent, { documentId, documentName, documentType });
};

export const processGlobalAnalyticsEvent = (
  originalEvent: GlobalAnalyticsEvent,
  extraParams: GlobalAnalyticsEventExtraParams,
): void => {
  originalEvent.stopPropagation();

  const { event } = originalEvent.detail;

  switch (event) {
    case 'refinementStateChanged': {
      const { isChecked } = originalEvent.detail;

      send(refinementStateChangedEvent, {
        isChecked,
        ...extraParams,
      });
      break;
    }
    default:
      break;
  }
};

export const triggerCtaClickEvent = (
  ctaClickGAParams:
  RangeCtaClickEventParams | MediaGalleryCtaClickEventParams | CtaClickEventParams,
): void => {
  if (!isAnalyticsEnabled()) {
    console.warn(`[PES]: Could not send ${events.CTA_CLICK} GA event. Analytics are disabled.`);
    return;
  }

  send(ctaClickEvent, ctaClickGAParams);
};
